export interface TransformationItem {
  icon: string
  title: string
  text: string
}

export const beforeItems: TransformationItem[] = [
  {
    icon: '😰',
    title: 'You Have the Information… But You’re Not Always Sure What to Do With It',
    text: 'You have photographs, radiographs, clinical findings and patient history in front of you — but turning all of that into a structured diagnosis isn’t always straightforward.',
  },
  {
    icon: '⏳',
    title: 'You Spend Too Much Time Analyzing Each Case',
    text: 'You move back and forth between different records, findings and clinical considerations because you don’t have a consistent process to follow.',
  },
  {
    icon: '🤔',
    title: 'You’re Not Sure Which Finding Should Come First',
    text: 'A patient can present with multiple clinical problems at the same time. Determining what is primary, secondary and urgent can be challenging.',
  },
  {
    icon: '🔎',
    title: 'You’re Worried About Missing Something Important',
    text: 'One overlooked finding can change your diagnosis, your treatment priorities or the way you approach the entire case.',
  },
  {
    icon: '📉',
    title: 'Some Cases Become More Complicated Than They Appeared',
    text: 'What seemed like a straightforward case can reveal additional factors once treatment planning begins.',
  },
  {
    icon: '😓',
    title: 'You Rely Too Much on Experience or Intuition',
    text: 'You want a repeatable clinical framework for unfamiliar or complex cases instead of starting from scratch every time.',
  },
]

export const afterItems: TransformationItem[] = [
  {
    icon: '✅',
    title: 'Follow a 7-Step Diagnostic Framework',
    text: 'Instead of wondering where to start, follow a structured sequence designed to guide you through the diagnostic process.',
  },
  {
    icon: '🧠',
    title: 'Know Exactly What to Evaluate',
    text: 'Use clinical protocols and checklists to systematically review the information that matters before making a decision.',
  },
  {
    icon: '🔎',
    title: 'Identify What Deserves Priority',
    text: 'Learn how to organize multiple findings and determine which ones should influence your clinical decision-making first.',
  },
  {
    icon: '📋',
    title: 'Reduce Diagnostic Omissions',
    text: 'Professional checklists make your evaluation more consistent and reduce the risk of overlooking relevant findings.',
  },
  {
    icon: '🗺️',
    title: 'Know What to Do Next',
    text: 'Clinical decision trees take you from: Finding → Interpretation → Priority → Clinical Decision.',
  },
  {
    icon: '🎯',
    title: 'Build a Clearer Diagnostic Pathway',
    text: 'Turn a case filled with information into a structured clinical process you can repeat with patient after patient.',
  },
]

export const soundsLikeYou: { icon: string; title: string; text: string }[] = [
  {
    icon: '😰',
    title: 'You finish evaluating a patient and still wonder if you missed something important.',
    text: 'You want a structured process that helps you systematically review the case before reaching a conclusion.',
  },
  {
    icon: '⏳',
    title: 'You spend too much time organizing the information from each patient.',
    text: 'Instead of starting from scratch, you want a repeatable framework you can apply in everyday practice.',
  },
  {
    icon: '🤔',
    title: 'You’re not always sure which clinical finding should take priority.',
    text: 'Your patient may have several problems at once, and you need a clearer way to determine what matters most.',
  },
  {
    icon: '🔍',
    title: 'You struggle to connect all the pieces of the clinical picture.',
    text: 'Examination, photographs, radiographs, history and complaints each tell you something different. You need a method to bring them together.',
  },
  {
    icon: '⚠️',
    title: 'You want more confidence before deciding on a treatment approach.',
    text: 'You don’t want to rush from identifying a problem directly into choosing a solution.',
  },
  {
    icon: '😓',
    title: 'You wish you had a diagnostic protocol you could actually use in your practice.',
    text: 'Something practical to consult whenever you’re evaluating a routine, unfamiliar or complex dental case.',
  },
]
